import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '../../state/ApiContext';
import { agentSwitchTarget, projectSwitchTarget } from './hierarchy-navigation';
import type { AgentHierarchySection } from './hierarchy-navigation';
import type { ConsoleHierarchyOption } from './types';

export interface HierarchyOptionsResult {
  options: ConsoleHierarchyOption[];
  loading: boolean;
  error: string | null;
  retry: () => void;
}

interface CachedOption {
  id: number;
  label: string;
}

interface HierarchyOptionState {
  options: CachedOption[];
  loading: boolean;
  error: string | null;
  requested: boolean;
}

interface ConsoleHierarchyOptionsContextValue {
  teamState: HierarchyOptionState;
  projectStates: Record<number, HierarchyOptionState>;
  agentStates: Record<number, HierarchyOptionState>;
  loadTeams: (force?: boolean) => void;
  loadProjects: (teamId: number, force?: boolean) => void;
  loadAgents: (teamId: number, force?: boolean) => void;
}

const emptyState = (): HierarchyOptionState => ({
  options: [],
  loading: false,
  error: null,
  requested: false,
});

const ConsoleHierarchyOptionsContext =
  createContext<ConsoleHierarchyOptionsContextValue | null>(null);

const unavailableOptionsContext: ConsoleHierarchyOptionsContextValue = {
  teamState: emptyState(),
  projectStates: {},
  agentStates: {},
  loadTeams: () => undefined,
  loadProjects: () => undefined,
  loadAgents: () => undefined,
};

function cacheOptions(
  records: Array<{ id?: number; name?: string }>,
): CachedOption[] {
  return records.flatMap((record) =>
    record.id !== undefined && record.name
      ? [{ id: record.id, label: record.name }]
      : [],
  );
}

export const ConsoleHierarchyOptionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { projectsApi, teamApi } = useApi();
  const [teamState, setTeamState] = useState<HierarchyOptionState>(emptyState);
  const [projectStates, setProjectStates] = useState<
    Record<number, HierarchyOptionState>
  >({});
  const [agentStates, setAgentStates] = useState<
    Record<number, HierarchyOptionState>
  >({});
  const teamStateRef = useRef(teamState);
  const projectStatesRef = useRef(projectStates);
  const agentStatesRef = useRef(agentStates);
  const inFlightRef = useRef(new Map<string, Promise<unknown>>());
  const generationRef = useRef(new Map<string, number>());

  const replaceTeamState = useCallback((state: HierarchyOptionState) => {
    teamStateRef.current = state;
    setTeamState(state);
  }, []);

  const replaceProjectState = useCallback(
    (teamId: number, state: HierarchyOptionState) => {
      const next = { ...projectStatesRef.current, [teamId]: state };
      projectStatesRef.current = next;
      setProjectStates(next);
    },
    [],
  );

  const replaceAgentState = useCallback(
    (teamId: number, state: HierarchyOptionState) => {
      const next = { ...agentStatesRef.current, [teamId]: state };
      agentStatesRef.current = next;
      setAgentStates(next);
    },
    [],
  );

  const loadTeams = useCallback(
    (force = false) => {
      const cacheKey = 'teams';
      if (
        !force &&
        (teamStateRef.current.requested || inFlightRef.current.has(cacheKey))
      ) {
        return;
      }

      const generation = (generationRef.current.get(cacheKey) ?? 0) + 1;
      generationRef.current.set(cacheKey, generation);
      replaceTeamState({
        ...teamStateRef.current,
        error: null,
        loading: true,
        requested: true,
      });

      const request = Promise.resolve().then(() => teamApi.apiTeamsGet());
      inFlightRef.current.set(cacheKey, request);
      void request
        .then((response) => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceTeamState({
            options: cacheOptions(response.data),
            error: null,
            loading: false,
            requested: true,
          });
        })
        .catch(() => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceTeamState({
            options: [],
            error: 'Failed to load teams',
            loading: false,
            requested: true,
          });
        })
        .finally(() => {
          if (inFlightRef.current.get(cacheKey) === request) {
            inFlightRef.current.delete(cacheKey);
          }
        });
    },
    [replaceTeamState, teamApi],
  );

  const loadProjects = useCallback(
    (teamId: number, force = false) => {
      const cacheKey = `projects:${teamId}`;
      const state = projectStatesRef.current[teamId] ?? emptyState();
      if (!force && (state.requested || inFlightRef.current.has(cacheKey))) {
        return;
      }

      const generation = (generationRef.current.get(cacheKey) ?? 0) + 1;
      generationRef.current.set(cacheKey, generation);
      replaceProjectState(teamId, {
        ...state,
        error: null,
        loading: true,
        requested: true,
      });

      const request = Promise.resolve().then(() =>
        projectsApi.apiTeamsTeamIdProjectsGet(teamId),
      );
      inFlightRef.current.set(cacheKey, request);
      void request
        .then((response) => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceProjectState(teamId, {
            options: cacheOptions(response.data.projects ?? []),
            error: null,
            loading: false,
            requested: true,
          });
        })
        .catch(() => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceProjectState(teamId, {
            options: [],
            error: 'Failed to load projects',
            loading: false,
            requested: true,
          });
        })
        .finally(() => {
          if (inFlightRef.current.get(cacheKey) === request) {
            inFlightRef.current.delete(cacheKey);
          }
        });
    },
    [projectsApi, replaceProjectState],
  );

  const loadAgents = useCallback(
    (teamId: number, force = false) => {
      const cacheKey = `agents:${teamId}`;
      const state = agentStatesRef.current[teamId] ?? emptyState();
      if (!force && (state.requested || inFlightRef.current.has(cacheKey))) {
        return;
      }

      const generation = (generationRef.current.get(cacheKey) ?? 0) + 1;
      generationRef.current.set(cacheKey, generation);
      replaceAgentState(teamId, {
        ...state,
        error: null,
        loading: true,
        requested: true,
      });

      const request = Promise.resolve().then(() =>
        teamApi.apiTeamTeamIdAgentsGet(teamId),
      );
      inFlightRef.current.set(cacheKey, request);
      void request
        .then((response) => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceAgentState(teamId, {
            options: cacheOptions(response.data),
            error: null,
            loading: false,
            requested: true,
          });
        })
        .catch(() => {
          if (generationRef.current.get(cacheKey) !== generation) return;
          replaceAgentState(teamId, {
            options: [],
            error: 'Failed to load agents',
            loading: false,
            requested: true,
          });
        })
        .finally(() => {
          if (inFlightRef.current.get(cacheKey) === request) {
            inFlightRef.current.delete(cacheKey);
          }
        });
    },
    [replaceAgentState, teamApi],
  );

  const value = useMemo(
    () => ({
      agentStates,
      loadAgents,
      loadProjects,
      loadTeams,
      projectStates,
      teamState,
    }),
    [
      agentStates,
      loadAgents,
      loadProjects,
      loadTeams,
      projectStates,
      teamState,
    ],
  );

  return (
    <ConsoleHierarchyOptionsContext.Provider value={value}>
      {children}
    </ConsoleHierarchyOptionsContext.Provider>
  );
};

function useHierarchyOptionsContext(): ConsoleHierarchyOptionsContextValue {
  const context = useContext(ConsoleHierarchyOptionsContext);
  return context ?? unavailableOptionsContext;
}

export function useTeamHierarchyOptions(
  currentTeamId: number,
  targetForTeam: (teamId: number) => string,
): HierarchyOptionsResult {
  const { loadTeams, teamState } = useHierarchyOptionsContext();

  useEffect(() => loadTeams(), [loadTeams]);

  return {
    options: teamState.options
      .filter((option) => option.id !== currentTeamId)
      .map((option) => ({ ...option, to: targetForTeam(option.id) })),
    loading: teamState.loading,
    error: teamState.error,
    retry: () => loadTeams(true),
  };
}

export function useProjectHierarchyOptions(
  teamId: number,
  currentProjectId: number,
): HierarchyOptionsResult {
  const { loadProjects, projectStates } = useHierarchyOptionsContext();
  const state = projectStates[teamId] ?? emptyState();

  useEffect(() => loadProjects(teamId), [loadProjects, teamId]);

  return {
    options: state.options
      .filter((option) => option.id !== currentProjectId)
      .map((option) => ({
        ...option,
        to: projectSwitchTarget(teamId, option.id),
      })),
    loading: state.loading,
    error: state.error,
    retry: () => loadProjects(teamId, true),
  };
}

export function useAgentHierarchyOptions(
  teamId: number,
  currentAgentId: number,
  section: AgentHierarchySection | null,
): HierarchyOptionsResult {
  const { agentStates, loadAgents } = useHierarchyOptionsContext();
  const state = agentStates[teamId] ?? emptyState();

  useEffect(() => loadAgents(teamId), [loadAgents, teamId]);

  return {
    options: state.options
      .filter((option) => option.id !== currentAgentId)
      .map((option) => ({
        ...option,
        to: agentSwitchTarget(teamId, option.id, section),
      })),
    loading: state.loading,
    error: state.error,
    retry: () => loadAgents(teamId, true),
  };
}
